import { firebaseFuncs, firebaseDb } from './firebase'
import { Functions, Collection, Role } from '../config/enums'
import { VendorUser, vendorUserConverter } from '../models/VendorUser'
import { vendorConverter, Vendor } from '../models/Vendor'
import { Variant, Item, itemConverter, Installment } from '../models/Item'
import { DeliveryOption } from '../models/Item'
//import { convertToPercentage } from '../utility/transformDiscount'
import { stringArrayToImageArray } from '../utility/imageToStringUrl'
import {
  installmentsToInstallmentOptions,
  isStraight
} from '../utility/installmentHelper'

/**
 * Retrieves vendor profile (VendorUser object) from Firebase Firestore
 */
export const getProfile = async (
  userId: string
): Promise<VendorUser | null> => {
  const doc = await firebaseDb
    .collection(Collection.vendorUsers)
    .doc(userId)
    .withConverter(vendorUserConverter)
    .get()

  if (doc.exists) {
    return doc.data() as VendorUser
  } else {
    return null
  }
}

/**
 * Calls `signupVendor()` Firebase Function and returns a new `Vendor` object
 */
export const signup = async (
  companyName: string,
  adminFirstName: string,
  adminLastName: string
): Promise<VendorUser | null> => {
  const signupFunc = firebaseFuncs.httpsCallable(Functions.signupVendor)

  try {
    const params: any = {
      companyName,
      adminFirstName,
      adminLastName
    }

    const res = await signupFunc(params)
    console.log('signupVendor res:', res)

    if (res.data) {
      return res.data.vendorUser as VendorUser
    } else {
      return null
    }
  } catch (err) {
    console.error(err)
    return null
  }
}

/**
 * Calls `updateVendorUser()` Firebase Function and returns a new `VendorUser` object
 */
export const editProfile = async (
  firstName: string,
  lastName: string,
  avatarUrl?: string
): Promise<VendorUser | null> => {
  const updateVendorUserFunc = firebaseFuncs.httpsCallable(
    Functions.updateVendorUser
  )

  let params: any = {
    firstName,
    lastName
  }
  if (avatarUrl) {
    params.avatarUrl = avatarUrl
  }

  const res = await updateVendorUserFunc(params)
  console.log('updateVendorUser res:', res)

  if (res.data) {
    return res.data.vendorUser as VendorUser
  } else {
    return null
  }
}

/**
 * Retrieves vendor company profile (Vendor object) from Firebase Firestore
 */
export const getCompanyProfile = async (
  vendorId: string
): Promise<Vendor | null> => {
  try {
    const doc = await firebaseDb
      .collection(Collection.vendors)
      .doc(vendorId)
      .withConverter(vendorConverter)
      .get()

    if (doc.exists) {
      return doc.data() as Vendor
    } else {
      return null
    }
  } catch (err) {
    console.error(err)
    return null
  }
}

/**
 * Retrieves vendor company profile (Vendor object) from Firebase Firestore
 */
export const getItemListing = async (itemId: string): Promise<Item | null> => {
  try {
    const doc = await firebaseDb
      .collection(Collection.items)
      .doc(itemId)
      .withConverter(itemConverter)
      .get()

    if (doc.exists) {
      return doc.data() as Item
    } else {
      return null
    }
  } catch (err) {
    console.error(err)
    return null
  }
}

/**
 * Calls `updateVendor()` Firebase Function and returns a new `Vendor` object
 */
export const editCompanyProfile = async (
  countryId: string,
  logoUrl?: string,
  email?: string,
  phonenumber?: string,
  description?: string
): Promise<Vendor | null> => {
  const updateVendorFunc = firebaseFuncs.httpsCallable(Functions.updateVendor)

  let params: any = {
    originCountryId: countryId
  }
  if (logoUrl) {
    params.logoUrl = logoUrl
  }
  if (email) {
    params.email = email
  }
  if (phonenumber) {
    params.phonenumber = phonenumber
  }
  if (description) {
    params.bio = description
  }

  const res = await updateVendorFunc(params)
  console.log('updateVendor res:', res)

  if (res.data) {
    return res.data.vendor as Vendor
  } else {
    return null
  }
}

/**
 * Calls `inviteVendorUser()` Firebase Function and returns a new `Vendor` object
 */
export const invite = async (
  firstName: string,
  lastName: string,
  email: string,
  role: Role
): Promise<VendorUser | null> => {
  const params: any = {
    firstName,
    lastName,
    email,
    role
  }

  const inviteFunc = firebaseFuncs.httpsCallable(Functions.inviteVendorUser)
  const res = await inviteFunc(params)
  console.log('inviteVendorUser res:', res)

  if (res.data) {
    return res.data.vendorUser as VendorUser
  } else {
    return null
  }
}

export const inviteSignup = async (
  email: string,
  password: string,
  inviteToken: string
): Promise<VendorUser | null> => {
  const params: any = {
    email,
    password,
    inviteToken
  }

  const signupFunc = firebaseFuncs.httpsCallable(Functions.signupVendorUser)
  const res = await signupFunc(params)

  console.log('signupVendorUser res:', res)

  if (res.data) {
    return res.data.vendorUser as VendorUser
  } else {
    return null
  }
}

export const isEmailInvited = async (email: string) => {
  const doc = await firebaseDb
    .collection(Collection.vendorUsers)
    .where('email', '==', email)
    .where('inviteToken', '>', '')
    .get()

  return doc.size > 0
}

export const deleteUser = async (userId: string) => {
  const params = { id: userId }
  const deleteFunc = firebaseFuncs.httpsCallable(Functions.deleteVendorUser)
  await deleteFunc(params)
}

export const createItem = async (
  title: string,
  description: string,
  variants: Variant[],
  installmentTerms: Installment[],
  isActive: boolean,
  deliveryOptions: DeliveryOption[],
  categoryIds: string[],
  images: string[],
  giftBundleId: string
) => {
  let params: any = {
    title,
    description,
    variants,
    isActive,
    deliveryOptions,
    categoryIds,
    images
  }
  params.images = stringArrayToImageArray(images)

  if (giftBundleId) {
    params.giftBundleId = giftBundleId
  }

  // Edited temporarily to accomodate new changes
  params.options = ['price', 'description']

  if (installmentTerms) {
    if (installmentTerms && variants) {
      params.installmentOptions = isStraight(installmentTerms)
        ? {}
        : installmentsToInstallmentOptions(installmentTerms, variants)
    }
  }

  const createListingFunc = firebaseFuncs.httpsCallable(
    Functions.createItemListing
  )
  const res = await createListingFunc(params)

  console.log('createListingRes', res)
}

export const updateItem = async (
  id: string,
  title?: string,
  description?: string,
  variants?: Variant[],
  installmentTerms?: Installment[],
  isActive?: boolean,
  deliveryOptions?: DeliveryOption[],
  categoryIds?: string[],
  images?: string[],
  giftBundleId?: string
) => {
  let params: any = {
    id,
    title,
    description,
    variants,
    isActive,
    deliveryOptions,
    categoryIds,
    images
  }

  if (title) {
    params.title = title
  }

  if (description) {
    params.description = description
  }

  if (variants) {
    params.variants = variants
    params.options = ['price', 'description']
  }

  if (isActive !== null) {
    params.isActive = isActive
  }

  if (deliveryOptions) {
    params.deliveryOptions = deliveryOptions
  }

  if (categoryIds) {
    params.categoryIds = categoryIds
  }

  if (images) {
    params.images = stringArrayToImageArray(images)
  }

  if (installmentTerms && variants) {
    params.installmentOptions = isStraight(installmentTerms)
      ? {}
      : installmentsToInstallmentOptions(installmentTerms, variants)
  }

  params.giftBundleId = giftBundleId ? giftBundleId : null

  const updateListingFunc = firebaseFuncs.httpsCallable(
    Functions.updateItemListing
  )
  const res = await updateListingFunc(params)
  console.log('updateListingRes', res)
}

export const deleteItem = async (itemId: string) => {
  const params = { id: itemId }
  const deleteFunc = firebaseFuncs.httpsCallable(Functions.deleteItemListing)
  await deleteFunc(params)
}
